import { PageAttr } from "../../../../shared/domain/entities/fetch-entities";
import { useConcatPages } from "../../../../shared/infrastructure";
import { useFetch } from "../../../../public/auth/infrastructure/store/fetch-context";
import {
  DispatcherDivisionT,
  DispatcherT,
  UseGetDispatcherDivisionFilters,
  UseGetDispatcherFilters,
} from "../../domain";
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";

const dispatchKeys = {
  all: ["dispatch"] as const,
  dispatchers: () => [...dispatchKeys.all, "dispatchers-list"] as const,
  dispatchersList: (filters: UseGetDispatcherFilters) =>
    [...dispatchKeys.dispatchers(), filters] as const,
  dispatchDivisions: () =>
    [...dispatchKeys.all, "dispatch-divisions-list"] as const,
  dispatchDivisionList: (filters: UseGetDispatcherDivisionFilters) =>
    [...dispatchKeys.dispatchDivisions(), filters] as const,
};

const DISPATCH_ENDPOINT = "dispatchers";
const DISPATCH_DIVISIONS_ENDPOINT = "dispatch-divisions";

const useInfiniteGetDispatchers = (
  filters: UseGetDispatcherFilters,
  config: UseInfiniteQueryOptions<PageAttr<DispatcherT>> = {}
): UseInfiniteQueryResult<PageAttr<DispatcherT>> & {
  allPages: PageAttr<DispatcherT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<DispatcherT>>(
    dispatchKeys.dispatchersList(filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<DispatcherT>>(
        DISPATCH_ENDPOINT,
        {
          params: {
            page,
            limit: 10,
            sort_by: "dispatcher",
            ...filters,
          },
        }
      );

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length > 0 ? allPages.length + 1 : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

const useInfiniteGetDispatcherDivisions = (
  filters: UseGetDispatcherDivisionFilters,
  config: UseInfiniteQueryOptions<PageAttr<DispatcherDivisionT>> = {}
): UseInfiniteQueryResult<PageAttr<DispatcherDivisionT>> & {
  allPages: PageAttr<DispatcherDivisionT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<DispatcherDivisionT>>(
    dispatchKeys.dispatchDivisionList(filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<DispatcherDivisionT>>(
        DISPATCH_DIVISIONS_ENDPOINT,
        {
          params: {
            page,
            limit: 10,
            ...filters,
          },
        }
      );

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length > 0 ? allPages.length + 1 : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

export {
  dispatchKeys,
  useInfiniteGetDispatchers,
  useInfiniteGetDispatcherDivisions,
};
